import Toggle from '@amzn/meridian/toggle';
import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import featureFlagService from 'src/services/featureFlagService';
import { updateFeatureFlags } from 'src/store/userSlice';
import ConfirmModal from 'src/components/shared/confirmModal/confirmModal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Heading from '@amzn/meridian/heading';

const DISPLAY_FEATURE_NAMES: Readonly<Record<string, string>> = {
  PLACEMENT_TRANSFER: 'Placement Transfer',
  DECISION_PORTAL: 'Decision Portal',
  ATOZ_NOTIFICATIONS: 'AtoZ Notifications',
  JOB_MODIFICATIONS: 'Job Modifications',
  CORRESPONDENCE: 'Correspondence',
  PERSONA: 'Persona',
};

type ModalState =
  | { isOpen: false; description: string }
  | { isOpen: true; description: string; featureName: string; isEnabled: boolean };

const FeatureFlags = () => {
  const dispatch = useAppDispatch();
  const { featureFlags } = useAppSelector((state) => state.user);
  const [disableToggles, setDisableToggles] = useState(false);
  const [modalConfirmationState, setModalConfirmationState] = useState<ModalState>({
    isOpen: false,
    description: '',
  });

  const handleToggleClick = (featureDisplayName: string, featureName: string, isEnabled: boolean) => () => {
    setModalConfirmationState({
      isOpen: true,
      description: `Are you sure you want to ${isEnabled ? 'enable' : 'disable'} the ${featureDisplayName} feature?`,
      featureName,
      isEnabled,
    });
  };

  const toggleFeature = () => {
    if (!modalConfirmationState.isOpen) return;

    const { featureName, isEnabled } = modalConfirmationState;
    setDisableToggles(true);

    const apiFeatureName = featureName.toLowerCase();
    featureFlagService
      .updateFeatureFlag(apiFeatureName, isEnabled)
      .then((res) => {
        dispatch(updateFeatureFlags(res.data));
      })
      .catch((error) => {
        // Handle error - maybe show error toast
        // eslint-disable-next-line no-console
        console.error('Failed to update feature flag:', error);
      })
      .finally(() => {
        setModalConfirmationState({
          isOpen: false,
          description: '',
        });
        setDisableToggles(false);
      });
  };

  const closeModal = () => {
    setModalConfirmationState({
      isOpen: false,
      description: '',
    });
  };

  return (
    <div
      style={{
        maxWidth: '300px',
        minWidth: '300px',
        margin: '2rem 0 0 2rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '1.5rem',
      }}
    >
      <Heading level={3}>Feature Flags</Heading>
      {Object.entries(featureFlags).map(([featureName, isEnabled]) => {
        const featureDisplayName = DISPLAY_FEATURE_NAMES[featureName];
        return (
          <Row
            key={featureName}
            alignmentHorizontal="justify"
            alignmentVertical="center"
            type="fill"
            spacingInset="400"
          >
            <Text>{featureDisplayName}</Text>
            <Toggle
              checked={isEnabled}
              onChange={handleToggleClick(featureDisplayName, featureName, !isEnabled)}
              disabled={disableToggles}
            />
          </Row>
        );
      })}
      <ConfirmModal
        modalDescription={modalConfirmationState.description}
        open={modalConfirmationState.isOpen}
        setOpen={closeModal} // this prop is required but it is only used to close the modal in this instance
        onConfirm={toggleFeature}
        showCancelButton
      />
    </div>
  );
};

export default FeatureFlags;
